import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { BiCheck } from "react-icons/bi";
import { MdClose } from "react-icons/md";
import Button from "shared/components/Button";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { GEO_FORMATS } from "./constants";
import StepCard from "./StepCard";

const DATE_FIELD_KEYS = new Set(["date_debut", "date_fin"]);

const formatSampleValue = (val, fieldKey) => {
  if (!DATE_FIELD_KEYS.has(fieldKey)) return val;
  const num = Number(val);
  if (Number.isNaN(num) || num <= 365 || num >= 200000) return val;
  const d = new Date((num - 25569) * 86400000);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const TARIF_FIELD_KEY = "tarif_ttc";

const StepMapping = ({
  fields,
  headers,
  autoMapping,
  mapping: localMapping,
  columnSamples,
  format,
  onConfirm,
  onMappingChange,
  loading,
  priceInCents,
  onPriceInCentsChange,
  onBack,
  backLabel,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const IMPORT = t("IMPORT", { returnObjects: true });
  const FIELD_LABELS = IMPORT.MAPPING.FIELDS;

  const usedColumns = new Set(Object.values(localMapping).filter(Boolean));

  const visibleFields = fields;

  const isFieldRequired = (f) =>
    f.required ||
    (f.geo === GEO_FORMATS.ZONIER && format === GEO_FORMATS.ZONIER) ||
    (f.geo === GEO_FORMATS.CODE_POSTAL && format === GEO_FORMATS.CODE_POSTAL);

  const requiredFields = visibleFields.filter(isFieldRequired);

  const mappedRequired = requiredFields.filter(
    (f) => localMapping[f.key],
  ).length;

  const handleFieldChange = (fieldKey, columnName) => {
    onMappingChange({
      ...localMapping,
      [fieldKey]: columnName || undefined,
    });
  };

  const handleClear = (fieldKey) => {
    const next = { ...localMapping };
    delete next[fieldKey];
    onMappingChange(next);
  };

  const columnOptions = headers.map((h) => ({
    label: usedColumns.has(h) ? `${h} (${IMPORT.MAPPING.USED})` : h,
    value: h,
    disabled:
      usedColumns.has(h) &&
      !Object.entries(localMapping).some(([, v]) => v === h),
  }));

  const confirmButton = (
    <Button
      type="primary"
      onClick={() => onConfirm(localMapping)}
      disabled={mappedRequired < requiredFields.length || loading}
      loading={loading}
    >
      <BiCheck size={20} />
      {IMPORT.MAPPING.CONFIRM}
    </Button>
  );

  return (
    <StepCard
      title={IMPORT.MAPPING.TITLE}
      description={IMPORT.MAPPING.DESCRIPTION}
      onBack={onBack}
      backLabel={backLabel}
      actions={confirmButton}
    >
      <div className="pricers-import__progress-bar">
        <span className="pricers-import__progress-label">
          {mappedRequired}/{requiredFields.length}{" "}
          {IMPORT.MAPPING.REQUIRED_MAPPED}
        </span>
        <div className="pricers-import__progress-track">
          <div
            className={classNames("pricers-import__progress-fill", {
              "pricers-import__progress-fill--complete":
                mappedRequired === requiredFields.length,
            })}
            style={{
              width: `${(mappedRequired / requiredFields.length) * 100}%`,
            }}
          />
        </div>
      </div>

      <table className="pricers-table">
        <thead>
          <tr className="pricers-table__header-row">
            <th className="pricers-table__th">
              {IMPORT.MAPPING.EXPECTED_FIELD}
            </th>
            <th className="pricers-table__th">{IMPORT.MAPPING.FILE_COLUMN}</th>
            <th className="pricers-table__th">{IMPORT.MAPPING.PREVIEW}</th>
          </tr>
        </thead>
        <tbody>
          {visibleFields.map((field) => {
            const translationKey = field.key.toUpperCase();
            const isMapped = !!localMapping[field.key];
            const isRequired = isFieldRequired(field);

            const isTarifField = field.key === TARIF_FIELD_KEY;

            return (
              <tr key={field.key} className="pricers-table__row">
                <td className="pricers-table__td">
                  <div className="d-flex align--center gap-8">
                    <span
                      className={classNames("pricers-import__status-dot", {
                        "pricers-import__status-dot--mapped": isMapped,
                        "pricers-import__status-dot--required":
                          !isMapped && isRequired,
                        "pricers-import__status-dot--optional":
                          !isMapped && !isRequired,
                      })}
                    />
                    <span className="pricers-import__field-label">
                      {FIELD_LABELS[translationKey]}
                    </span>
                    {isRequired && (
                      <span className="pricers-import__required-badge">
                        {IMPORT.MAPPING.REQUIRED}
                      </span>
                    )}
                  </div>
                  {isTarifField && isMapped && (
                    <label className="d-flex align--center gap-6 mt-4">
                      <input
                        type="checkbox"
                        checked={priceInCents}
                        onChange={(e) => onPriceInCentsChange(e.target.checked)}
                        className="pricers-checkbox"
                      />
                      <span className="pricers-import__field-hint">
                        {IMPORT.MAPPING.PRICE_IN_CENTS}
                      </span>
                    </label>
                  )}
                </td>
                <td className="pricers-table__td">
                  <div className="d-flex align--center gap-8">
                    <Select
                      showSearch
                      options={columnOptions}
                      value={localMapping[field.key] || undefined}
                      placeholder={IMPORT.MAPPING.SELECT_COLUMN}
                      onChange={(val) => handleFieldChange(field.key, val)}
                      allowClear
                      className="pricers-import__mapping-select"
                    />
                    {isMapped && (
                      <MdClose
                        size={18}
                        color={COLORS.C_GRAY}
                        className="pricers-import__clickable"
                        onClick={() => handleClear(field.key)}
                      />
                    )}
                  </div>
                </td>
                <td className="pricers-table__td">
                  <div className="d-flex gap-4 flex--wrap">
                    {(columnSamples[localMapping[field.key]] || [])
                      .slice(0, 4)
                      .map((val, i) => (
                        <span key={i} className="pricers-import__sample-tag">
                          {formatSampleValue(val, field.key)}
                        </span>
                      ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </StepCard>
  );
};

export default StepMapping;
